'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMessages, useTranslations } from 'next-intl';
import {
  Authenticator as AmplifyAuthenticator,
  AuthenticatorProps as AmplifyAuthenticatorProps,
  Button,
  ThemeProvider,
  translations,
  useAuthenticator,
} from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';
import { signUp, confirmSignUp } from 'aws-amplify/auth';
import { Alert, AlertTitle, IconButton, Button as MuiButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { Messages, isNorthAmerica, useCurrentLocale } from '@peerless/ui-locale';

import { amplifyTheme } from './theme.amplify';

export interface AuthenticatorProps {
  children?: React.ReactNode;
}

// load translations into Amplify for use in the Authenticator
I18n.putVocabularies(translations);

// configure order of form fields in the sign-up form
const formFields: AmplifyAuthenticatorProps['formFields'] = {
  signUp: {
    given_name: {
      order: 1,
      isRequired: true,
    },
    family_name: {
      order: 2,
      isRequired: true,
    },
    email: {
      order: 3,
      isRequired: true,
    },
    'custom:company': {
      order: 4,
      isRequired: true,
      label: 'Company',
      placeholder: 'Enter your Company Name',
    },
    'custom:job_title': {
      label: 'Job Title',
      placeholder: 'Enter your Job Title',
      order: 5,
    },
    password: {
      order: 7,
      isRequired: true,
    },
    confirm_password: {
      order: 8,
      isRequired: true,
    },
  },
};

/**
 * Wraps a component tree with authentication via Amplify.
 */
export function Authenticator({ children }: AuthenticatorProps) {
  const { locale, language, region } = useCurrentLocale();
  const messages = useMessages() as Messages;
  const { toForgotPassword, toSignIn } = useAuthenticator();

  const [showSignInMessage, setShowSignInMessage] = useState(false);

  const authFields = useMemo(() => {
    if (region !== 'UK') return formFields;

    return {
      signUp: {
        ...formFields!.signUp,
        'custom:country': {
          label: 'Country',
          placeholder: 'Enter your Country',
          order: 6,
          isRequired: true,
        },
      },
    };
  }, [region]);

  useEffect(() => {
    // sync the selected locale with Amplify
    // .. amplify includes translations, see for ex., https://ui.docs.amplify.aws/react/connected-components/authenticator/customization#internationalization-i18n
    I18n.setLanguage(language);

    // load any custom translations or labels from our localization dictionary into Amplify
    I18n.putVocabulariesForLanguage(language, messages['Amplify']);
  }, [language, messages]);

  const services: AmplifyAuthenticatorProps['services'] = useMemo(() => {
    return {
      handleSignUp(input) {
        return signUp({
          ...input,
          options: {
            ...input.options,
            userAttributes: {
              ...input.options?.userAttributes,
              // add the current locale to the user's attributes
              locale,
            },
          },
        });
      },
      async handleConfirmSignUp(input) {
        const output = await confirmSignUp(input);

        if (output.nextStep.signUpStep !== 'COMPLETE_AUTO_SIGN_IN') {
          setShowSignInMessage(true);
          toSignIn();
        }

        return output;
      },
    };
  }, [locale, toSignIn]);

  const t = useTranslations('Account');

  const FormFields = useCallback(() => {
    return (
      <>
        <AmplifyAuthenticator.SignUp.FormFields />
        <Alert severity={'info'}>{t('password-requirements')}</Alert>
      </>
    );
  }, [t]);

  const Footer = useCallback(() => {
    return (
      <>
        <Button fontWeight="bold" onClick={toForgotPassword} size="small" variation="link">
          {t('forgot-password')}
        </Button>
        {isNorthAmerica(region) && (
          <>
            <Typography>
              <Typography fontWeight={'bold'}>{t('business-1')}</Typography>
              {t('business-2')}
            </Typography>

            <MuiButton
              href="https://peerless-av-portal.acumatica.com/Frames/Login.aspx"
              target="_blank"
              variant="outlined"
              color="secondary"
            >
              {t('b2b-login')}
            </MuiButton>
          </>
        )}
      </>
    );
  }, [t, region, toForgotPassword]);

  const Header = useCallback(() => {
    if (!showSignInMessage) return null;

    return (
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => setShowSignInMessage(false)}
          >
            <FontAwesomeIcon icon={icons.close} fixedWidth />
          </IconButton>
        }
      >
        <AlertTitle>{t('sign-in-message')}</AlertTitle>
      </Alert>
    );
  }, [t, showSignInMessage]);

  return (
    <ThemeProvider theme={amplifyTheme}>
      <AmplifyAuthenticator.Provider>
        <AmplifyAuthenticator
          components={{
            SignUp: {
              FormFields,
            },
            SignIn: {
              Header,
              Footer,
            },
          }}
          formFields={authFields}
          services={services}
          initialState="signIn"
        >
          {children}
        </AmplifyAuthenticator>
      </AmplifyAuthenticator.Provider>
    </ThemeProvider>
  );
}
